/**
 * One section of the profile.
 *
 * Expects the section's catalog entries, the values the api sent keyed by catalog key, the two
 * filters, the years the switch offers and the year it has on, the callback the page decides a
 * value with, and the notices the page put on these rows.
 *
 * Renders the section heading, the year switch in the one section showTaxYearSwitch names, and a
 * table whose header is Field / Value, an unlabelled column for the value's yellow tag (named
 * "Needs attention" for a screen reader), Where it came from and an unlabelled Approval column,
 * and whose body is one FieldRow per catalog entry the filters leave on screen. Below Tailwind's
 * lg width the header and each row are three-column grids instead (FIELD_TABLE_LAYOUT in
 * FieldRow.tsx), and the tag and Where it came from headers are hidden, because those two cells
 * sit under the value.
 *
 * The api answers with the values of one tax year at a time, so a person has to be able to say
 * which year, and the page draws that switch in one section only. A section that holds a value kept
 * per tax year and does not draw the switch names the chosen year in its heading, as in 'Workforce
 * and payroll · 2024', so the year is readable where the values are; the section that draws the
 * switch leaves the year out of its heading, because the switch's pressed button beside that
 * heading already reads the year.
 */

import type {
  TaxProfileFieldDefinition,
  TaxProfileFieldGroup,
} from '@shared/constants/taxProfileFields';
import type { TaxProfileFieldValue } from '@shared/types/taxProfile';
import { Spinner } from '@/components/Spinner';
import { fieldIsShown } from '@/pages/TaxProfile/fieldState';
import { SECTION_FILTERED_OUT_SENTENCE } from '@/pages/TaxProfile/wording';
import { noticeKey } from '@/pages/TaxProfile/useProfileFieldWrites';
import FieldRow, {
  FIELD_TABLE_LAYOUT,
  FieldRowNotice,
  fieldRowElementId,
} from '@/pages/TaxProfile/components/FieldRow';
import type { FieldDecision } from '@/pages/TaxProfile/components/ValuePicker';
import { BRAND_HEADING_CLASS } from '@/pages/TaxProfile/brand';

/** A value and the catalog entry that describes it, as the page hands them around together. */
export type ProfileFieldEntry = {
  field: TaxProfileFieldValue;
  definition: TaxProfileFieldDefinition;
};

export interface ProfileGroupProps {
  groupKey: TaxProfileFieldGroup;
  label: string;
  /** The catalog entries of this section, in catalog order. */
  definitions: ReadonlyArray<TaxProfileFieldDefinition>;
  /** What the api sent for each of those keys. */
  fieldsByKey: Record<string, TaxProfileFieldValue>;
  needsReviewOnly: boolean;
  hideEmpty: boolean;
  taxYears: number[];
  taxYear: number | null;
  onTaxYearChange: (taxYear: number) => void;
  /** True in the one section the page draws the year switch in. */
  showTaxYearSwitch: boolean;
  /** Writes what a person decided about one value. */
  onDecide?: (
    field: TaxProfileFieldValue,
    definition: TaxProfileFieldDefinition,
    decision: FieldDecision
  ) => void;
  /** Opens the file of a document named on a line or in its panel. */
  onOpenSourceDocument?: (documentId: number) => void;
  /** What each row says after an act a person can take back, keyed by noticeKey. */
  noticesByKey?: Record<string, FieldRowNotice>;
  /** True while a write is in flight: the rows' buttons wait for it. */
  busy?: boolean;
  /**
   * True while the answer for the year just picked on the switch is on its way. The rows on screen
   * are still the previous year's, so a spinner is drawn beside the switch.
   */
  taxYearLoading?: boolean;
  /**
   * The element id "Go to field" just sent the page to (fieldRowElementId); the row it names
   * carries HIGHLIGHT_ROW_CLASS. Null when there is none.
   */
  highlightedElementId?: string | null;
  /** Added to the section's own classes; TaxProfile.tsx passes the glow of the section in view. */
  className?: string;
}

/** The id the section index jumps to. */
export const groupAnchorId = (groupKey: string) => `tax-profile-${groupKey}`;

/**
 * The years the switch offers: the current tax year and the one before it, together with every year
 * the api sent, newest first and each one once.
 *
 * The current tax year and the one before it are always there because a client whose documents have
 * not been read yet has no tax_profile_yearly_values row, so the api sends no year at all. Without
 * those two, no year would be drawn, every per-year row would carry no year, and saving one would
 * be answered "This value belongs to a tax year, which the request does not name". The
 * tax_profile_yearly_values row for the year chosen here is created by the first write to it.
 */
export const offeredTaxYears = (
  taxYears: number[],
  currentTaxYear: number
): number[] =>
  [...new Set([currentTaxYear, currentTaxYear - 1, ...taxYears])].sort(
    (one, other) => other - one
  );

/**
 * The values the accept-all button accepts: the ones nobody has approved whose sources all give one
 * value ('proposed' with one waiting group). A value with several different values waiting, and an
 * approved value with a different value waiting, need a person to choose.
 */
export const singleValueEntries = (
  entries: ProfileFieldEntry[]
): ProfileFieldEntry[] =>
  entries.filter(
    ({ field }) => field.status === 'proposed' && field.waiting.length === 1
  );

/**
 * A section's heading: 'Workforce and payroll · 2024' where the section holds values kept per tax
 * year. The section that draws the year switch keeps its plain label, because the switch's pressed
 * button sits beside the heading and already reads the year. The Profile view of the page
 * (ProfileView.tsx) titles its cards with it too.
 */
export const sectionHeading = (
  label: string,
  definitions: ReadonlyArray<TaxProfileFieldDefinition>,
  taxYear: number | null,
  drawsTaxYearSwitch: boolean
): string =>
  definitions.some((definition) => definition.yearly) &&
  taxYear !== null &&
  !drawsTaxYearSwitch
    ? `${label} · ${taxYear}`
    : label;

/**
 * The year switch: one button per year, oldest to newest, the pressed one the year shown, and a
 * spinner before them while the answer for a newly picked year is on its way. ProfileGroup draws
 * it beside the heading of the one section showTaxYearSwitch names, and the Profile view of the
 * page (ProfileView.tsx) beside the heading of the same section's card.
 */
export function TaxYearSwitch({
  taxYears,
  taxYear,
  onTaxYearChange,
  taxYearLoading = false,
}: {
  taxYears: number[];
  taxYear: number | null;
  onTaxYearChange: (taxYear: number) => void;
  taxYearLoading?: boolean;
}) {
  return (
    <div className='flex items-center gap-2'>
      {taxYearLoading && (
        <span role='status' aria-label={`Loading ${taxYear ?? ''}`}>
          <Spinner className='w-4 h-4' />
        </span>
      )}
      <div className='btn-group' role='group' aria-label='Tax year'>
        {/* taxYears comes newest first (offeredTaxYears), and TaxProfile.tsx shows its first
            year by default; the buttons run oldest to newest, left to right. */}
        {[...taxYears]
          .sort((one, other) => one - other)
          .map((year) => (
            <button
              key={year}
              type='button'
              className={`btn btn-xs ${year === taxYear ? 'btn-active' : ''}`}
              aria-pressed={year === taxYear}
              onClick={() => onTaxYearChange(year)}
            >
              {year}
            </button>
          ))}
      </div>
    </div>
  );
}

export default function ProfileGroup({
  groupKey,
  label,
  definitions,
  fieldsByKey,
  needsReviewOnly,
  hideEmpty,
  taxYears,
  taxYear,
  onTaxYearChange,
  showTaxYearSwitch,
  onDecide,
  onOpenSourceDocument,
  noticesByKey = {},
  busy = false,
  taxYearLoading = false,
  highlightedElementId = null,
  className = '',
}: ProfileGroupProps) {
  /** True where this section carries the year switch and there is at least one year to offer. */
  const drawsTaxYearSwitch = showTaxYearSwitch && taxYears.length > 0;

  const heading = sectionHeading(
    label,
    definitions,
    taxYear,
    drawsTaxYearSwitch
  );

  const shown: ProfileFieldEntry[] = definitions
    .map((definition) => ({ definition, field: fieldsByKey[definition.key] }))
    .filter(({ field }) => fieldIsShown(field, { needsReviewOnly, hideEmpty }));

  return (
    <section
      id={groupAnchorId(groupKey)}
      className={`mb-6 scroll-mt-4 ${className}`}
    >
      <div className='flex items-center justify-between mb-1 gap-2'>
        <h3 className={`font-bold ${BRAND_HEADING_CLASS}`}>{heading}</h3>
        {drawsTaxYearSwitch ? (
          <TaxYearSwitch
            taxYears={taxYears}
            taxYear={taxYear}
            onTaxYearChange={onTaxYearChange}
            taxYearLoading={taxYearLoading}
          />
        ) : (
          <div className='flex items-center gap-2' />
        )}
      </div>
      {shown.length === 0 ? (
        <p className='text-xs opacity-70'>{SECTION_FILTERED_OUT_SENTENCE}</p>
      ) : (
        // From lg up, table-fixed and the header widths decide the columns, not the cells' content,
        // so a value's tag or its open panel never moves the columns after it. The panel hangs out
        // of the Value cell (position absolute in FieldRow.tsx), so it takes no width from the table.
        <table className={FIELD_TABLE_LAYOUT.table}>
          <thead className={FIELD_TABLE_LAYOUT.headSection}>
            <tr className={FIELD_TABLE_LAYOUT.row}>
              <th className={FIELD_TABLE_LAYOUT.fieldHeader}>Field</th>
              <th className={FIELD_TABLE_LAYOUT.valueHeader}>Value</th>
              <th
                className={FIELD_TABLE_LAYOUT.pillHeader}
                aria-label='Needs attention'
              />
              <th className={FIELD_TABLE_LAYOUT.sourceHeader}>
                Where it came from
              </th>
              <th
                className={FIELD_TABLE_LAYOUT.approvalHeader}
                aria-label='Approval'
              />
            </tr>
          </thead>
          <tbody className={FIELD_TABLE_LAYOUT.section}>
            {shown.map(({ definition, field }) => (
              <FieldRow
                key={definition.key}
                definition={definition}
                field={field}
                onDecide={onDecide}
                onOpenSourceDocument={onOpenSourceDocument}
                notice={
                  noticesByKey[noticeKey(definition.key, field.taxYear)] ?? null
                }
                busy={busy}
                highlighted={
                  highlightedElementId === fieldRowElementId(definition.key)
                }
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
